import { Link } from 'react-router'
import { lifespan } from '../../../shared/tree'
import { Ellipsis, UserMinus } from 'lucide-react'
import { useState } from 'react'
import { ActionMenu } from '@/components/ActionMenu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { FormError } from '@/components/FormError'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { accountLink, useDisconnect } from '@/lib/connections'
import type { ShownPerson } from '@/lib/peopleList'
import { usePerson } from '@/lib/tree'

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

// Someone's profile, opened from a card on People: their face, what they are to you, the circles you share, and what
// their page holds, then the way to their page. Someone who joined a circle without a family tree has no page yet.
export function PersonProfile({ person, onClose }: { person: ShownPerson | null; onClose: () => void }) {
  return (
    <Dialog open={!!person} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-sm">
        {person && <Profile person={person} />}
      </DialogContent>
    </Dialog>
  )
}

function Profile({ person }: { person: ShownPerson }) {
  const personId = person.href?.split('/').at(-1) ?? ''
  const page = usePerson(personId, { enabled: !!person.href })
  const years = lifespan(person)
  const holds = page.data
    ? [
        page.data.photoCount > 0 && plural(page.data.photoCount, 'photo'),
        page.data.momentCount > 0 && plural(page.data.momentCount, 'moment'),
        page.data.recipes.length > 0 && plural(page.data.recipes.length, 'recipe'),
      ].filter((x): x is string => !!x)
    : []
  const about = [
    person.line,
    !person.living && 'In memory',
    years,
    person.joined && person.line !== 'You' && person.line !== 'Connected' && 'In the app',
  ].filter(Boolean)
  return (
    <div className="flex flex-col items-center gap-5 text-center">
      <Avatar person={person} variant="display" className="size-28 text-5xl" />
      <DialogHeader className="items-center text-center sm:text-center">
        <DialogTitle className="text-title text-balance">{person.name}</DialogTitle>
        <DialogDescription>{about.join(' · ') || 'In your circles'}</DialogDescription>
      </DialogHeader>
      {person.circleList && person.circleList.length > 0 && (
        <ul aria-label={`${person.name}’s circles`} className="flex flex-wrap justify-center gap-1.5">
          {person.circleList.map((circle) => (
            <li key={circle.id}>
              <Link
                to={`/circle/${circle.id}`}
                className="flex min-h-8 items-center rounded-full bg-secondary px-3 text-xs font-medium outline-none hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 pointer-coarse:min-h-11"
              >
                {circle.name}
              </Link>
            </li>
          ))}
        </ul>
      )}
      {holds.length > 0 && <p className="text-sub text-muted-foreground">{holds.join(' · ')}</p>}
      {/* Their page: on the family tree when they're on it, else their own (everything of theirs you can see). Someone
          on the tree with an account has both. */}
      {person.href || person.userId ? (
        <Button asChild className="w-full">
          <Link to={person.href ?? accountLink(person.userId!)}>Open {person.name}’s page</Link>
        </Button>
      ) : (
        <p className="text-sub text-muted-foreground">
          {person.name} isn’t on a family tree yet, so there’s no page to open.
        </p>
      )}
      {person.href && person.userId && (
        <Button asChild variant="outline" className="w-full">
          <Link to={accountLink(person.userId)}>Everything {person.name} shares</Link>
        </Button>
      )}
    </div>
  )
}

// Their page's "…" menu, which holds ending a one-to-one connection, out of the way of just looking (mobile review,
// Dallas 2026-10-18). It still asks first. They aren't told; you each stop seeing what the other shares with Everyone.
export function ConnectionOptions({ userId, name }: { userId: string; name: string }) {
  const end = useDisconnect()
  const [asking, setAsking] = useState(false)
  return (
    <>
      <ActionMenu
        title={`Options for ${name}`}
        align="end"
        trigger={
          <Button variant="ghost" size="icon" aria-label={`Options for ${name}`} disabled={end.isPending}>
            <Ellipsis aria-hidden />
          </Button>
        }
        groups={[[{ label: 'Remove connection', icon: UserMinus, onSelect: () => setAsking(true) }]]}
      />
      <FormError error={end.error} />
      <AlertDialog open={asking} onOpenChange={setAsking}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove your connection with {name}?</AlertDialogTitle>
            <AlertDialogDescription>
              You’ll each stop seeing what the other shares with Everyone. They aren’t told, and either of you can ask
              to connect again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep</AlertDialogCancel>
            <AlertDialogAction onClick={() => end.mutate(userId)}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
